import { ArrowLeft, Download, Lock, Save, Send, Trash2, XCircle } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useBlocker, useNavigate, useParams } from 'react-router-dom';
import {
  calculerTotauxFacture,
  montantEnLettres,
  VILLE_DEFAUT,
  type Client,
  type Facture,
  type LigneFactureInput,
} from '@sirajoptic/shared';
import { BadgeStatutFacture } from '@/components/ui/badge';
import { Button, variantesBouton } from '@/components/ui/button';
import { Card, CardHeader } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { InputField, TextareaField } from '@/components/ui/field';
import { ChargementBloc, Encart, EtatErreur } from '@/components/ui/feedback';
import { useToast } from '@/components/ui/toast';
import { ClientAutocomplete } from '@/features/clients/components/ClientAutocomplete';
import { ApiError } from '@/lib/api-client';
import { aujourdhui, dh } from '@/lib/format';
import {
  useAnnulerFacture,
  useCreerFacture,
  useEmettreFacture,
  useFacture,
  useModifierFacture,
  useSupprimerFacture,
} from './api/factures.api';
import { LignesFacture } from './components/LignesFacture';
import { telechargerFacture } from './pdf/facture-pdf';

const CHAMPS_ORDONNANCE = [
  { cle: 'od_loin_sph', bloc: 'loin', oeil: 'OD', mesure: 'SPH' },
  { cle: 'od_loin_cyl', bloc: 'loin', oeil: 'OD', mesure: 'CYL' },
  { cle: 'og_loin_sph', bloc: 'loin', oeil: 'OG', mesure: 'SPH' },
  { cle: 'og_loin_cyl', bloc: 'loin', oeil: 'OG', mesure: 'CYL' },
  { cle: 'od_pres_sph', bloc: 'pres', oeil: 'OD', mesure: 'SPH' },
  { cle: 'od_pres_cyl', bloc: 'pres', oeil: 'OD', mesure: 'CYL' },
  { cle: 'og_pres_sph', bloc: 'pres', oeil: 'OG', mesure: 'SPH' },
  { cle: 'og_pres_cyl', bloc: 'pres', oeil: 'OG', mesure: 'CYL' },
] as const;

type CleOrdonnance = (typeof CHAMPS_ORDONNANCE)[number]['cle'];

type Formulaire = {
  reference: string;
  date_facture: string;
  ville: string;
  client_id: string | null;
  client_nom: string;
  client_ice: string;
  ordonnance: Record<CleOrdonnance, string>;
  lignes: LigneFactureInput[];
};

function vide(): Formulaire {
  return {
    reference: '',
    date_facture: aujourdhui(),
    ville: VILLE_DEFAUT,
    client_id: null,
    client_nom: '',
    client_ice: '',
    ordonnance: Object.fromEntries(CHAMPS_ORDONNANCE.map((c) => [c.cle, ''])) as Record<
      CleOrdonnance,
      string
    >,
    lignes: [],
  };
}

function depuisFacture(f: Facture): Formulaire {
  return {
    reference: f.reference,
    date_facture: f.date_facture,
    ville: f.ville,
    client_id: f.client_id,
    client_nom: f.client_nom,
    client_ice: f.client_ice ?? '',
    ordonnance: Object.fromEntries(
      CHAMPS_ORDONNANCE.map((c) => [c.cle, f[c.cle] ?? '']),
    ) as Record<CleOrdonnance, string>,
    lignes: f.lignes.map((l) => ({
      product_id: l.product_id,
      designation: l.designation,
      quantite: l.quantite,
      prix_unitaire_ht: l.prix_unitaire_ht,
      taux_tva: l.taux_tva,
    })),
  };
}

export function FactureEditPage() {
  const { id } = useParams<{ id: string }>();
  const naviguer = useNavigate();
  const toast = useToast();

  const { data: facture, isPending, error, refetch } = useFacture(id);
  const creer = useCreerFacture();
  const modifier = useModifierFacture();
  const emettre = useEmettreFacture();
  const annuler = useAnnulerFacture();
  const supprimer = useSupprimerFacture();

  const [form, setForm] = useState<Formulaire>(vide);
  /** État de départ, pour repérer une saisie non enregistrée. */
  const [initial, setInitial] = useState<Formulaire>(vide);
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const [aEmettre, setAEmettre] = useState(false);
  const [aAnnuler, setAAnnuler] = useState(false);
  const [motif, setMotif] = useState('');
  const [aSupprimer, setASupprimer] = useState(false);

  useEffect(() => {
    if (!facture) return;
    const depart = depuisFacture(facture);
    setForm(depart);
    // Ce qui vient d'être enregistré devient la nouvelle référence.
    setInitial(depart);
  }, [facture]);

  // L'aperçu utilise la fonction du serveur : impossible de diverger.
  const totaux = useMemo(
    () =>
      calculerTotauxFacture(
        form.lignes.map((l) => ({
          quantite: l.quantite,
          prix_unitaire_ht: l.prix_unitaire_ht,
          taux_tva: l.taux_tva,
        })),
      ),
    [form.lignes],
  );

  const modifie = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initial),
    [form, initial],
  );

  /* Les départs volontaires — après enregistrement, après suppression — passent
   * par ce drapeau. Un `useState` arriverait trop tard : le blocage est
   * consulté dans le même tour que la navigation. */
  const sortieVoulue = useRef(false);

  const blocage = useBlocker(
    ({ currentLocation, nextLocation }) =>
      modifie && !sortieVoulue.current && currentLocation.pathname !== nextLocation.pathname,
  );

  /* Le routeur ne voit passer ni la fermeture d'onglet ni le rechargement :
   * le navigateur pose sa propre demande de confirmation. */
  useEffect(() => {
    if (!modifie) return;
    const surDechargement = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', surDechargement);
    return () => window.removeEventListener('beforeunload', surDechargement);
  }, [modifie]);

  if (id && isPending) return <ChargementBloc libelle="Chargement de la facture…" />;
  if (id && error) return <EtatErreur erreur={error} onReessayer={() => void refetch()} />;

  const modifiable = !facture || facture.statut === 'BROUILLON';
  const emise = facture?.statut === 'EMISE';
  const annulee = facture?.statut === 'ANNULEE';

  function set<K extends keyof Formulaire>(cle: K, valeur: Formulaire[K]) {
    setForm((f) => ({ ...f, [cle]: valeur }));
  }

  function charge() {
    const ordonnance = Object.fromEntries(
      CHAMPS_ORDONNANCE.map((c) => [c.cle, form.ordonnance[c.cle].trim() || null]),
    );
    return {
      reference: form.reference.trim() || undefined,
      date_facture: form.date_facture,
      ville: form.ville.trim() || VILLE_DEFAUT,
      client_id: form.client_id,
      client_nom: form.client_nom.trim(),
      client_ice: form.client_ice.trim() || null,
      commande_id: null,
      lignes: form.lignes,
      ...ordonnance,
    };
  }

  function valider(): boolean {
    const e: Record<string, string> = {};
    if (!form.client_nom.trim()) e.client_nom = 'Le nom du client est obligatoire.';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date_facture)) e.date_facture = 'Date invalide.';
    setErreurs(e);
    return Object.keys(e).length === 0;
  }

  async function enregistrer(): Promise<Facture | null> {
    if (!valider()) return null;
    try {
      const enregistree = facture
        ? await modifier.mutateAsync({ id: facture.id, patch: charge() })
        : await creer.mutateAsync(charge() as Parameters<typeof creer.mutateAsync>[0]);

      toast.succes('Brouillon enregistré.');
      if (!facture) {
        sortieVoulue.current = true;
        naviguer(`/factures/${enregistree.id}`, { replace: true });
      }
      return enregistree;
    } catch (e) {
      if (e instanceof ApiError) {
        setErreurs(e.details ?? { reference: e.message });
        toast.erreur(e.message);
      } else {
        toast.erreur("La facture n'a pas pu être enregistrée.");
      }
      return null;
    }
  }

  async function confirmerEmission() {
    // On enregistre d'abord : ce qui part en stock doit être ce qui est affiché.
    const enregistree = await enregistrer();
    const cible = enregistree ?? facture;
    if (!cible) return;

    try {
      await emettre.mutateAsync(cible.id);
      toast.succes('Facture émise. Le stock a été mis à jour.');
      setAEmettre(false);
    } catch (e) {
      toast.erreur(e instanceof Error ? e.message : "L'émission a échoué.");
    }
  }

  async function confirmerAnnulation() {
    if (!facture || !motif.trim()) return;
    try {
      await annuler.mutateAsync({ id: facture.id, motif: motif.trim() });
      toast.succes('Facture annulée. Le stock a été restitué.');
      setAAnnuler(false);
      setMotif('');
    } catch (e) {
      toast.erreur(e instanceof Error ? e.message : "L'annulation a échoué.");
    }
  }

  async function confirmerSuppression() {
    if (!facture) return;
    try {
      await supprimer.mutateAsync(facture.id);
      toast.succes('Brouillon supprimé.');
      sortieVoulue.current = true;
      naviguer('/factures');
    } catch (e) {
      toast.erreur(e instanceof Error ? e.message : 'Suppression impossible.');
    }
  }

  async function telecharger() {
    if (!facture) {
      toast.info('Enregistrez le brouillon avant de générer le PDF.');
      return;
    }
    try {
      await telechargerFacture(facture);
    } catch {
      toast.erreur("Le PDF n'a pas pu être généré.");
    }
  }

  const enCours = creer.isPending || modifier.isPending;

  return (
    <>
      <PageEntete
        facture={facture}
        modifiable={modifiable}
        emise={emise}
        enCours={enCours}
        onEnregistrer={() => void enregistrer()}
        onEmettre={() => setAEmettre(true)}
        onTelecharger={() => void telecharger()}
        onAnnuler={() => setAAnnuler(true)}
        onSupprimer={() => setASupprimer(true)}
      />

      <div className="p-4 sm:p-6">
        {emise ? (
          <Encart ton="succes" titre="Facture émise" className="mb-4">
            Ce document est définitif : il ne peut plus être modifié ni supprimé, seulement annulé.
          </Encart>
        ) : null}

        {annulee ? (
          <Encart ton="danger" titre="Facture annulée" className="mb-4">
            {facture?.motif_annulation
              ? `Motif : ${facture.motif_annulation}`
              : 'Le stock a été restitué.'}
          </Encart>
        ) : null}

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <div className="space-y-4 xl:col-span-2">
            <Card>
              <CardHeader titre="Client et référence" />
              <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
                <ClientAutocomplete
                  nom={form.client_nom}
                  onChangeNom={(nom) => set('client_nom', nom)}
                  onSelectionner={(client: Client | null) => set('client_id', client?.id ?? null)}
                  erreur={erreurs.client_nom}
                  requis
                />
                <InputField
                  label="ICE du client"
                  value={form.client_ice}
                  onChange={(e) => set('client_ice', e.target.value)}
                  disabled={!modifiable}
                  aide="Facultatif — clients professionnels."
                />
                <InputField
                  label="Référence"
                  value={form.reference}
                  onChange={(e) => set('reference', e.target.value)}
                  placeholder="Générée automatiquement"
                  disabled={!modifiable}
                  erreur={erreurs.reference}
                  aide={
                    modifiable
                      ? 'Modifiable tant que la facture est en brouillon.'
                      : 'Figée à l’émission.'
                  }
                />
                <div className="grid grid-cols-2 gap-3">
                  <InputField
                    label="Date"
                    type="date"
                    value={form.date_facture}
                    onChange={(e) => set('date_facture', e.target.value)}
                    disabled={!modifiable}
                    erreur={erreurs.date_facture}
                    requis
                  />
                  <InputField
                    label="Ville"
                    value={form.ville}
                    onChange={(e) => set('ville', e.target.value)}
                    disabled={!modifiable}
                  />
                </div>
              </div>
            </Card>

            <Card>
              <CardHeader
                titre="Ordonnance"
                description="Reprise telle quelle dans les deux cases du document imprimé."
              />
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                <BlocOrdonnance
                  titre="Vision de loin"
                  bloc="loin"
                  valeurs={form.ordonnance}
                  onChange={(cle, v) => set('ordonnance', { ...form.ordonnance, [cle]: v })}
                  desactive={!modifiable}
                />
                <BlocOrdonnance
                  titre="Vision de près"
                  bloc="pres"
                  valeurs={form.ordonnance}
                  onChange={(cle, v) => set('ordonnance', { ...form.ordonnance, [cle]: v })}
                  desactive={!modifiable}
                />
              </div>
            </Card>

            <Card>
              <CardHeader
                titre="Lignes"
                description="Une ligne rattachée à un produit décrémentera le stock à l'émission."
              />
              <div className="p-4">
                <LignesFacture
                  lignes={form.lignes}
                  onChange={(lignes) => set('lignes', lignes)}
                  lectureSeule={!modifiable}
                />
              </div>
            </Card>
          </div>

          {/* Aperçu permanent des totaux et du montant en lettres. */}
          <div className="xl:sticky xl:top-6 xl:self-start">
            <Card>
              <CardHeader titre="Récapitulatif" />
              <div className="space-y-3 p-4">
                <dl className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-ink-muted">Total HT</dt>
                    <dd className="tabular">{dh(totaux.total_ht)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-ink-muted">TVA</dt>
                    <dd className="tabular">{dh(totaux.total_tva)}</dd>
                  </div>
                  <div className="flex justify-between border-t border-line pt-2 text-base font-semibold">
                    <dt>Total TTC</dt>
                    <dd className="tabular text-mustard-600">{dh(totaux.total_ttc)}</dd>
                  </div>
                </dl>

                <div className="rounded-md border border-line bg-surface-sunken p-3">
                  <p className="text-xs uppercase tracking-wide text-ink-muted">
                    Arrêtée à la somme de
                  </p>
                  <p className="mt-1 text-sm italic text-ink">
                    {montantEnLettres(totaux.total_ttc, { capitaliser: true })}
                  </p>
                </div>

                {modifiable && form.lignes.length === 0 ? (
                  <Encart ton="moutarde">
                    Ajoutez au moins une ligne avant d'émettre la facture.
                  </Encart>
                ) : null}
              </div>
            </Card>
          </div>
        </div>
      </div>

      <ConfirmDialog
        ouvert={aEmettre}
        onFermer={() => setAEmettre(false)}
        onConfirmer={() => void confirmerEmission()}
        titre="Émettre cette facture ?"
        libelleConfirmer="Émettre"
        chargement={emettre.isPending || enCours}
        message={
          <>
            <p>
              Le stock des lignes rattachées à un produit sera décrémenté, et la facture deviendra
              <strong> définitive</strong> : plus aucune modification ne sera possible.
            </p>
            <p className="mt-2">
              Total TTC : <strong>{dh(totaux.total_ttc)}</strong>
            </p>
          </>
        }
      />

      <ConfirmDialog
        ouvert={aAnnuler}
        onFermer={() => {
          setAAnnuler(false);
          setMotif('');
        }}
        onConfirmer={() => void confirmerAnnulation()}
        titre="Annuler cette facture ?"
        libelleConfirmer="Annuler la facture"
        danger
        chargement={annuler.isPending}
        message="Le stock sorti sera restitué. La facture reste consultable, avec sa mention d'annulation."
      >
        <TextareaField
          label="Motif de l'annulation"
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
          rows={2}
          requis
          erreur={aAnnuler && !motif.trim() ? 'Le motif est obligatoire.' : undefined}
        />
      </ConfirmDialog>

      <ConfirmDialog
        ouvert={aSupprimer}
        onFermer={() => setASupprimer(false)}
        onConfirmer={() => void confirmerSuppression()}
        titre="Supprimer ce brouillon ?"
        libelleConfirmer="Supprimer"
        danger
        chargement={supprimer.isPending}
        message="Ce brouillon n'a jamais été émis : sa suppression est sans conséquence sur le stock."
      />

      {/* Quitter la page avec une saisie en cours : le routeur attend ici. */}
      <ConfirmDialog
        ouvert={blocage.state === 'blocked'}
        onFermer={() => blocage.reset?.()}
        onConfirmer={() => blocage.proceed?.()}
        titre="Quitter sans enregistrer ?"
        libelleConfirmer="Quitter sans enregistrer"
        danger
        message="Les modifications apportées à cette facture ne sont pas enregistrées. Elles seront perdues."
      />
    </>
  );
}

function PageEntete({
  facture,
  modifiable,
  emise,
  enCours,
  onEnregistrer,
  onEmettre,
  onTelecharger,
  onAnnuler,
  onSupprimer,
}: {
  facture: Facture | undefined;
  modifiable: boolean;
  emise: boolean;
  enCours: boolean;
  onEnregistrer: () => void;
  onEmettre: () => void;
  onTelecharger: () => void;
  onAnnuler: () => void;
  onSupprimer: () => void;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line bg-white px-4 py-4 sm:px-6">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-xl font-semibold tracking-tight text-ink">
            {facture ? `Facture ${facture.reference}` : 'Nouvelle facture'}
          </h1>
          {facture ? <BadgeStatutFacture statut={facture.statut} /> : null}
          {!modifiable ? (
            <span className="inline-flex items-center gap-1 text-xs text-ink-muted">
              <Lock aria-hidden className="h-3 w-3" />
              Document verrouillé
            </span>
          ) : null}
        </div>
        <p className="mt-1 text-sm text-ink-muted">
          {facture ? `N° d'ordre ${facture.numero_ordre}` : 'Le numéro est attribué à la création.'}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Link to="/factures" className={variantesBouton({ variant: 'ghost', size: 'sm' })}>
          <ArrowLeft aria-hidden className="h-4 w-4" />
          Toutes les factures
        </Link>

        {facture ? (
          <Button
            variant="outline"
            icone={<Download aria-hidden className="h-4 w-4" />}
            onClick={onTelecharger}
          >
            Télécharger PDF
          </Button>
        ) : null}

        {modifiable ? (
          <>
            {facture ? (
              <Button
                variant="outline"
                icone={<Trash2 aria-hidden className="h-4 w-4" />}
                onClick={onSupprimer}
              >
                Supprimer
              </Button>
            ) : null}
            <Button
              variant="outline"
              icone={<Save aria-hidden className="h-4 w-4" />}
              onClick={onEnregistrer}
              chargement={enCours}
            >
              Enregistrer brouillon
            </Button>
            <Button
              variant="primary"
              icone={<Send aria-hidden className="h-4 w-4" />}
              onClick={onEmettre}
            >
              Émettre la facture
            </Button>
          </>
        ) : null}

        {emise ? (
          <Button
            variant="danger"
            icone={<XCircle aria-hidden className="h-4 w-4" />}
            onClick={onAnnuler}
          >
            Annuler la facture
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function BlocOrdonnance({
  titre,
  bloc,
  valeurs,
  onChange,
  desactive,
}: {
  titre: string;
  bloc: 'loin' | 'pres';
  valeurs: Record<CleOrdonnance, string>;
  onChange: (cle: CleOrdonnance, valeur: string) => void;
  desactive?: boolean;
}) {
  const champs = CHAMPS_ORDONNANCE.filter((c) => c.bloc === bloc);

  return (
    <div className="rounded-md border border-line">
      <p className="border-b border-line bg-surface-sunken px-3 py-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
        {titre}
      </p>
      <div className="grid grid-cols-2 gap-3 p-3">
        {champs.map((c) => (
          <InputField
            key={c.cle}
            label={`${c.oeil} ${c.mesure}`}
            value={valeurs[c.cle]}
            onChange={(e) => onChange(c.cle, e.target.value)}
            disabled={desactive}
            className="font-mono"
          />
        ))}
      </div>
    </div>
  );
}
